import baseStyled, { ThemedStyledInterface, css } from "styled-components";
const OFFSET_PC = 40;
const OFFSET_TABLET = 16;
const color = {
	black: "black",
	white: "white",
	gray: {
		light: "rgba(255,255,255,0.3)"
	},
	bg: "#202020",
	appBg: css`
		background: rgb(32, 32, 32); /* Old browsers */
		background: -moz-linear-gradient(-45deg, rgba(32, 32, 32, 1) 0%, rgba(20, 20, 20, 1) 57%, rgba(20, 20, 20, 1) 57%); /* FF3.6-15 */
		background: -webkit-linear-gradient(-45deg, rgba(32, 32, 32, 1) 0%, rgba(20, 20, 20, 1) 57%, rgba(20, 20, 20, 1) 57%); /* Chrome10-25,Safari5.1-6 */
		background: linear-gradient(135deg, rgba(32, 32, 32, 1) 0%, rgba(20, 20, 20, 1) 57%, rgba(20, 20, 20, 1) 57%); /* W3C, IE10+, FF16+, Chrome26+, Opera12+, Safari7+ */
		filter: progid:DXImageTransform.Microsoft.gradient( startColorstr='#202020', endColorstr='#141414',GradientType=1 ); /* IE6-9 fallback on horizontal gradient */
	`,
	logo: "#323232",
	default: "#3b3b3b",

	three: {
		default: "#a8acb3",
		white: "#f5f5f5",
		black: "#3b3b3b",

		blue: "#5d8dde",
		red: "#fa9084",
		yellow: "#fdff94",
		green: "#91ffaf",
		purple: "#d48fff"
	}
};

const div = {
	top: (color?: string, size?: number) => css`
		&:after {
			content: "";
			position: absolute;
			width: 100%;
			height: ${size ? `${size}px` : `2px`};
			top: 0;
			left: 0;
			background-color: ${color ? color : theme.color.black};
		}
	`,
	bottom: (color?: string, size?: number, width?: string, option?: any) => css`
		&:after {
			content: "";
			position: absolute;
			width: ${width ? width : "100%"};
			height: ${size ? `${size}px` : `2px`};
			bottom: 0;
			left: 0;
			background-color: ${color ? color : theme.color.black};
			${option}
		}
	`,
	left: (color?: string, size?: number) => css`
		&:after {
			content: "";
			position: absolute;
			width: ${size ? `${size}px` : `2px`};
			height: 100%;
			top: 0;
			left: 0;
			background-color: ${color ? color : theme.color.black};
		}
	`,
	right: (color?: string, size?: number) => css`
		&:after {
			content: "";
			position: absolute;
			width: ${size ? `${size}px` : `2px`};
			height: 100%;
			top: 0;
			right: 0;
			background-color: ${color ? color : theme.color.black};
		}
	`
};

const transition = {
	default: `all 0.5s cubic-bezier(0.7, 0, 0.24, 0.99);`,
	hover: `all 0.2s cubic-bezier(0.7, 0, 0.24, 0.99);`,
	popup: {
		duration: 1000
	}
};

const layout = {
	full_height: `calc(var(--vh, 1vh) * 100)`,
	full_height_layout: css`
		height: 100vh;
		height: calc(var(--vh, 1vh) * 100);
	`,
	full_abs: css`
		position: absolute;
		left: 0;
		top: 0;
		right: 0;
		bottom: 0;
	`,
	full_image: (url: string) => css`
		background-image: url("${url}");
		background-size: cover;
		background-position: center;`,

	center_flex: css`
		display: flex;
		align-items: center;
		justify-content: center;
	`,
	center_abs: css`
		position: absolute;
		left: 50%;
		top: 50%;
		transform: translate(-50%, -50%);
	`,

	ratio: (ratio: number) => css`
		&:after {
			content: "";
			padding-top: ${ratio ? ratio : 100}%;
			display: block;
		}
	`,
	scroll_hide: css`
		&::-webkit-scrollbar {
			display: none;
		}

		/* Hide scrollbar for IE, Edge and Firefox */
		-ms-overflow-style: none; /* IE and Edge */
		scrollbar-width: none; /* Firefox */
	`
};

const font = {
	inconsolata: `'Inconsolata', monospace, 'Noto Sans KR', sans-serif`
};
const text = {
	default: css`
		font-size: 16px;
		line-height: 1.6;
		font-weight: 400;
	`
};

const event = {
	disable: css`
		user-select: none;
		pointer-events: none;
	`,
	able: css`
		user-select: auto;
		pointer-events: auto;
	`
};

const zIndex = {
	canvas: 50,
	planetList: 55,
	homeBtn: 60,
	header: 80,
	dim: 90,
	popup: 95,
	menu: 100,
	supported_browsers: 120,
	cursor: 120,

	loading: 150,
	page: {
		default: 1,
		planet: 1
	},

	three: {
		logo: 0,
		planet_pos: {
			front: 300,
			back: 100
		}
	}
};

const size = {
	offset: {
		pc: OFFSET_PC,
		tablet: OFFSET_TABLET
	},
	logo: {
		pc: 64,
		tablet: 40
	},
	zoom: {
		controller: 16,
		bar: 200,
		max: 12,
		min: 2,
		init: 4
	},
	three: {
		planet_pos: 24,
		planet_dot: 16,
		planet_radius: 40,
		planet_orbitRadius: 600,
		listRatio: 65,
		listRatio_min: 100
	},
	cursor: {
		size: 40,
		gap: 6,
		moveArea: OFFSET_PC * 3
	},
	ratio: {
		source: 56
	},
	loading: {
		planet: 64
	},
	gallery: {
		gap: 2
	}
};

const theme = {
	color,
	div,
	transition,
	layout,
	zIndex,
	size,
	event,
	font,
	text
};

export type Theme = typeof theme;
export const styled = baseStyled as ThemedStyledInterface<Theme>;
export default theme;
